import React, { useRef, useState, useCallback, useContext } from "react";
import { useMediaQuery } from "react-responsive";

import { SectionContainer } from "../../components/SectionContainer";
import { Headline } from "../../components/Headline";
import { Tagline } from "../../components/Tagline/Tagline";
import { StructuredTextRenderer } from "../../components/StructuredTextRenderer";
import { Button } from "../../components/Button";
import { ImageWithGrid } from "../../components/Image";
import { RowRenderer } from "./RowRenderer/RowRenderer";

import GeneralPageContext from "../../templates/GeneralPageContext";
import ElementContext from "../../components/ElementContext";

import { useIsMobile, useIsSmaller1400 } from "../../lib/hooks/mediaQueries";
import { generateClassString as gcs } from "../../lib/helper/generateClassString";
import translate from "../../lib/translate";

import * as styles from "./component.module.less";

const MEDIA_LAYOUT_STANDARD = "standard_without_gallery";

function MediaTextAdvanced({
    data: {
        anker,
        tagline,
        headline,
        semanticOfHeadline,
        subheader,
        image,
        hideGrid,
        mediaAlignRight,
        extendButton,
        collapseButton,
        rows = [],
        background,
        mediaLayout = MEDIA_LAYOUT_STANDARD
    }
}) {
    const contentRef = useRef()
    const hasRows = rows.length > 0;
    const [extended, setExtended] = useState(!(extendButton && hasRows));

    const isMobile = useIsMobile();
    const isSmaller1400 = useIsSmaller1400();
    const smallAnchorContentWidth =
        useMediaQuery({ query: "(min-width: 1201px" }) && isSmaller1400;

    const { isLightBg } = useContext(ElementContext);

    const cmsContent = rows.map((row) => (
        <RowRenderer key={row.id} rowData={row} withGallery={mediaLayout} />
    ));

    const {
        layout: { showAnchorNavigation }
    } = useContext(GeneralPageContext);

    const expand = useCallback(() => {
        setExtended(true);
    }, [])
    
    const collapse = useCallback(() => {
        setExtended(false);

        if (contentRef.current) {
            contentRef.current.scrollIntoView({behavior: 'smooth'})
        }
    }, [contentRef.current])

    return (
        <SectionContainer
            ref={contentRef}
            sectionId={anker}
            background={background}
            sectionClass={styles.mediaTextAdvancedSection}
            containerClass={styles.mediaTextAdvancedContainer}
        >
            <div ref={contentRef}>
                {(image ||
                    tagline ||
                    headline ||
                    subheader.value.document.children[0].children[0].value !==
                        "") && (
                    <SectionContainer
                        renderAs="div"
                        mediaAlignRight={mediaAlignRight}
                        containerClass={gcs(
                            [hasRows, styles.topContainerFollowedByBottomContainer],
                            styles.topContainer
                        )}
                        sectionClass={styles.topContainerSection}
                    >
                        {image && (
                            <div className={styles.mediaContainer}>
                                <ImageWithGrid
                                    image={image}
                                    className={styles.mainImageContainer}
                                    gridDisable={hideGrid}
                                    gridSize={
                                        (showAnchorNavigation &&
                                            smallAnchorContentWidth) ||
                                        isMobile
                                            ? "s"
                                            : "m"
                                    }
                                />
                            </div>
                        )}

                        <div
                            className={gcs(styles.contentContainer, [
                                !image,
                                styles.noImg
                            ])}
                        >
                            {tagline && (
                                <Tagline hasLightBackground={isLightBg}>
                                    {tagline}
                                </Tagline>
                            )}

                            {headline && (
                                <Headline
                                    semanticOfHeadline={semanticOfHeadline}
                                    hasLightBackground={isLightBg}
                                    headlineClass={gcs( [!subheader, styles.noBottomMargin])}
                                >
                                    {headline}
                                </Headline>
                            )}

                            {subheader && (
                                <StructuredTextRenderer data={subheader} />
                            )}
                        </div>
                    </SectionContainer>
                )}

                {extendButton ? (
                    <div className={styles.bottomContainer}>
                        {!extended && (
                            <div
                                className={gcs(
                                    styles.mediaTextAdvancedExpandBlock,
                                    styles.notExtended
                                )}
                            >
                                <Button
                                    type="primary"
                                    icon="chevronDown"
                                    onClick={expand}
                                >
                                    {extendButton}
                                </Button>
                            </div>
                        )}

                        {extended && (
                            <>
                                {cmsContent}

                                <div
                                    className={styles.mediaTextAdvancedExpandBlock}
                                >
                                    <Button
                                        type="primary"
                                        icon="chevronUp"
                                        onClick={collapse}
                                    >
                                        {collapseButton ||
                                            translate(
                                                "media-text-advanced.collapse"
                                            )}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <>{cmsContent}</>
                )}
            </div>
        </SectionContainer>
    );
}

export { MediaTextAdvanced };
